import type { ErpModule } from '@/api/platform.api';
import { SETTINGS_PATH } from '@/config/settingsRegistry';
import type { ScreenActionGrants, ScreenPermissionModuleGroup } from '@/utils/screenPermissionMatrix';

export const MODULE_SETTINGS_PATH_PREFIX = '__module-settings__/';

export function moduleSettingsGrantPath(erpModule: ErpModule): string;
export function moduleSettingsGrantPath(erpModule: ErpModule, canonicalPath: string): string;
export function moduleSettingsGrantPath(
  erpModule: ErpModule,
  canonicalPath?: string,
): string {
  if (canonicalPath === undefined) {
    return `${MODULE_SETTINGS_PATH_PREFIX}${erpModule}`;
  }
  const canonical = canonicalPath.split('?')[0];
  if (canonical === SETTINGS_PATH || canonical === '') {
    return `${MODULE_SETTINGS_PATH_PREFIX}${erpModule}`;
  }
  return `${MODULE_SETTINGS_PATH_PREFIX}${erpModule}${canonical}`;
}

export function isModuleSettingsGrantPath(path: string): boolean {
  return path.startsWith(MODULE_SETTINGS_PATH_PREFIX);
}

export function parseModuleSettingsGrantPath(
  path: string,
): { erpModule: ErpModule; canonicalPath: string } | null {
  if (!isModuleSettingsGrantPath(path)) return null;
  const rest = path.slice(MODULE_SETTINGS_PATH_PREFIX.length);
  const slash = rest.indexOf('/');
  if (slash < 0) {
    return { erpModule: rest as ErpModule, canonicalPath: SETTINGS_PATH };
  }
  return {
    erpModule: rest.slice(0, slash) as ErpModule,
    canonicalPath: rest.slice(slash) || SETTINGS_PATH,
  };
}

export function isSettingsNavPath(path: string): boolean {
  const base = path.split('?')[0];
  return base === SETTINGS_PATH || base.startsWith(`${SETTINGS_PATH}/`);
}

/** Copy legacy `/settings` grants onto module-scoped virtual keys when loading stored roles. */
export function migrateLegacySettingsGrants(
  stored: ScreenActionGrants | undefined | null,
  matrix: ScreenPermissionModuleGroup[],
): ScreenActionGrants {
  if (!stored) return {};
  const migrated: ScreenActionGrants = { ...stored };
  const virtualPaths = new Set(matrix.flatMap((group) => group.screens.map((row) => row.path)));

  for (const [path, actions] of Object.entries(stored)) {
    if (isModuleSettingsGrantPath(path)) continue;
    if (path !== SETTINGS_PATH && !path.startsWith(`${SETTINGS_PATH}/`)) continue;
    for (const virtualPath of virtualPaths) {
      if (!virtualPath.startsWith(MODULE_SETTINGS_PATH_PREFIX)) continue;
      const parsed = parseModuleSettingsGrantPath(virtualPath);
      if (!parsed) continue;
      const canonical =
        path === SETTINGS_PATH ? SETTINGS_PATH : path;
      if (parsed.canonicalPath !== canonical) continue;
      if (migrated[virtualPath]) continue;
      migrated[virtualPath] = { ...actions };
    }
  }

  return migrated;
}

export function moduleSettingsViewGranted(
  canonicalPath: string,
  erpModule: ErpModule,
  grants: ScreenActionGrants | undefined | null,
): boolean {
  if (!grants) return false;
  const virtual = moduleSettingsGrantPath(erpModule, canonicalPath);
  if (grants[virtual]?.view === true) return true;
  const base = canonicalPath.split('?')[0];
  if (grants[base]?.view === true) return true;
  return false;
}

function isSettingsMasterPath(path: string): boolean {
  const parts = path.split('?')[0].split('/').filter(Boolean);
  return parts[0] === 'settings' && parts.length >= 3;
}

/**
 * Settings hubs are structural routes. They are viewable only when the active
 * module has an explicitly granted settings master beneath that hub.
 */
export function moduleSettingsContainerViewGranted(
  canonicalPath: string,
  erpModule: ErpModule,
  grants: ScreenActionGrants | undefined | null,
): boolean {
  if (!grants) return false;
  const base = canonicalPath.split('?')[0].replace(/\/+$/, '') || SETTINGS_PATH;
  const descendantPrefix = `${base}/`;

  return Object.entries(grants).some(([grantPath, actions]) => {
    if (actions?.view !== true) return false;
    const parsed = parseModuleSettingsGrantPath(grantPath);
    if (parsed) {
      const grantedPath = parsed.canonicalPath.split('?')[0];
      return (
        parsed.erpModule === erpModule &&
        isSettingsMasterPath(grantedPath) &&
        grantedPath.startsWith(descendantPrefix)
      );
    }
    const grantedPath = grantPath.split('?')[0];
    return isSettingsMasterPath(grantedPath) && grantedPath.startsWith(descendantPrefix);
  });
}

export function moduleSettingsActionGrants(
  canonicalPath: string,
  erpModule: ErpModule,
  grants: ScreenActionGrants | undefined | null,
) {
  if (!grants) return undefined;
  const virtual = moduleSettingsGrantPath(erpModule, canonicalPath);
  const base = canonicalPath.split('?')[0];
  return grants[virtual] ?? grants[base];
}

export function canViewModuleSettingsNav(
  navPath: string,
  activeModule: ErpModule,
  grants: ScreenActionGrants | undefined | null,
  screenGrantsConfigured: boolean,
): boolean | undefined {
  if (!screenGrantsConfigured) return undefined;
  if (!isSettingsNavPath(navPath)) return undefined;
  const base = navPath.split('?')[0];
  if (base === SETTINGS_PATH || base.split('/').filter(Boolean).length === 2) {
    return moduleSettingsContainerViewGranted(base, activeModule, grants);
  }
  return moduleSettingsViewGranted(base, activeModule, grants);
}
